import { useCallback, useEffect, useMemo, useState } from 'react';
import Icon from './Icon';
import Modal from './Modal';
import { getAccessToken, isSuperAdminContextUser } from '../authStorage';
import {
  createSuperAdminPackage,
  deleteSuperAdminPackage,
  fetchSuperAdminPackages,
  mapPackageFormToPayload,
  updateSuperAdminPackage,
} from '../superAdminPackagesApi';
import { useLanguage } from '../i18n/language';
import './BranchesPage.css';
import './Modal.css';
import './UsersPage.css';

const emptyForm = () => ({
  label: '',
  name: '',
  tagline: '',
  price: '',
  priceSub: '',
  boardSeatsText: '',
  officerSeatsText: '',
  shareholderSeatsText: '',
  featuresText: '',
  buttonText: '',
  buttonUrl: '',
  sortOrder: 0,
  isFeatured: false,
  isActive: true,
});

export default function SuperAdminPackagesPage() {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = useMemo(() => ({
    title: isAr ? 'إدارة الباقات' : 'Packages Management',
    sub: isAr ? 'إضافة وتعديل الباقات المعروضة في صفحة الهبوط.' : 'Manage packages shown on landing page.',
    add: isAr ? 'إضافة باقة' : 'Add package',
    edit: isAr ? 'تعديل' : 'Edit',
    remove: isAr ? 'حذف' : 'Delete',
    save: isAr ? 'حفظ' : 'Save',
    cancel: isAr ? 'إلغاء' : 'Cancel',
    loading: isAr ? 'جارٍ تحميل الباقات...' : 'Loading packages...',
    noResults: isAr ? 'لا توجد باقات.' : 'No packages found.',
    loadFailed: isAr ? 'تعذر تحميل الباقات' : 'Unable to load packages',
    saveFailed: isAr ? 'تعذر حفظ الباقة' : 'Unable to save package',
    deleteFailed: isAr ? 'تعذر حذف الباقة' : 'Unable to delete package',
    required: isAr ? 'املأ الحقول المطلوبة: التصنيف، الاسم، السعر، المقاعد، زر الإجراء.' : 'Required: label, name, price, seats and button text.',
    modalAdd: isAr ? 'إضافة باقة' : 'Add package',
    modalEdit: isAr ? 'تعديل باقة' : 'Edit package',
    lblLabel: isAr ? 'التصنيف' : 'Label',
    lblName: isAr ? 'اسم الباقة' : 'Package name',
    lblTagline: isAr ? 'وصف مختصر' : 'Tagline',
    lblPrice: isAr ? 'السعر' : 'Price',
    lblPriceSub: isAr ? 'وصف السعر' : 'Price subtitle',
    lblBoard: isAr ? 'مقاعد المجلس' : 'Board seats text',
    lblOfficer: isAr ? 'مقاعد الأمناء/الرئيس التنفيذي' : 'Officer seats text',
    lblShareholder: isAr ? 'مقاعد المساهمين' : 'Shareholder seats text',
    lblFeatures: isAr ? 'المميزات (كل سطر ميزة)' : 'Features (one per line)',
    lblButtonText: isAr ? 'نص الزر' : 'Button text',
    lblButtonUrl: isAr ? 'رابط الزر' : 'Button URL',
    lblSort: isAr ? 'الترتيب' : 'Sort order',
    lblFeatured: isAr ? 'باقة مميزة' : 'Featured package',
    lblActive: isAr ? 'نشطة للعرض في صفحة الهبوط' : 'Active on landing page',
    colName: isAr ? 'الباقة' : 'Package',
    colPrice: isAr ? 'السعر' : 'Price',
    colStatus: isAr ? 'الحالة' : 'Status',
    colSort: isAr ? 'الترتيب' : 'Sort',
    colActions: isAr ? 'إجراءات' : 'Actions',
    active: isAr ? 'نشطة' : 'Active',
    inactive: isAr ? 'غير نشطة' : 'Inactive',
    featured: isAr ? 'مميزة' : 'Featured',
  }), [isAr]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setItems(await fetchSuperAdminPackages());
    } catch (e) {
      setError(e instanceof Error ? e.message : t.loadFailed);
    } finally {
      setLoading(false);
    }
  }, [t.loadFailed]);

  useEffect(() => {
    if (!getAccessToken()) return;
    if (!isSuperAdminContextUser()) {
      setAccessDenied(true);
      setLoading(false);
      return;
    }
    loadData();
  }, [loadData]);

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm());
    setError('');
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditId(row.id);
    setForm({
      label: row.label || '',
      name: row.name || '',
      tagline: row.tagline || '',
      price: row.price || '',
      priceSub: row.priceSub || '',
      boardSeatsText: row.boardSeatsText || '',
      officerSeatsText: row.officerSeatsText || '',
      shareholderSeatsText: row.shareholderSeatsText || '',
      featuresText: (row.features || []).join('\n'),
      buttonText: row.buttonText || '',
      buttonUrl: row.buttonUrl || '',
      sortOrder: row.sortOrder || 0,
      isFeatured: !!row.isFeatured,
      isActive: !!row.isActive,
    });
    setError('');
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.label.trim() || !form.name.trim() || !form.price.trim() || !form.boardSeatsText.trim() || !form.officerSeatsText.trim() || !form.shareholderSeatsText.trim() || !form.buttonText.trim()) {
      setError(t.required);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const payload = mapPackageFormToPayload(form);
      if (editId) await updateSuperAdminPackage(editId, payload);
      else await createSuperAdminPackage(payload);
      setModalOpen(false);
      setEditId(null);
      setForm(emptyForm());
      await loadData();
    } catch (e2) {
      setError(e2 instanceof Error ? e2.message : t.saveFailed);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (row) => {
    const yes = window.confirm(`${t.remove}: ${row.name}?`);
    if (!yes) return;
    try {
      await deleteSuperAdminPackage(row.id);
      await loadData();
    } catch (e) {
      setError(e instanceof Error ? e.message : t.deleteFailed);
    }
  };

  if (accessDenied) return null;

  return (
    <div className="page users-page">
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editId ? t.modalEdit : t.modalAdd}
        subtitle={t.sub}
        icon={<Icon name="money" size={18} color="#fff" />}
        size="lg"
        variant="drawer-left"
        headerActions={(
          <button type="submit" form="package-modal-form" className="btn btn--primary" disabled={submitting}>
            {t.save}
          </button>
        )}
        footer={<button type="button" className="btn btn--ghost" onClick={() => setModalOpen(false)}>{t.cancel}</button>}
      >
        <form id="package-modal-form" className="mform" onSubmit={handleSave}>
          <div className="mform__row">
            <div className="mform__field"><label className="mform__label">{t.lblLabel}</label><input className="mform__input" value={form.label} onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))} /></div>
            <div className="mform__field"><label className="mform__label">{t.lblName}</label><input className="mform__input" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} /></div>
          </div>
          <div className="mform__row">
            <div className="mform__field"><label className="mform__label">{t.lblPrice}</label><input className="mform__input" value={form.price} onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))} /></div>
            <div className="mform__field"><label className="mform__label">{t.lblPriceSub}</label><input className="mform__input" value={form.priceSub} onChange={(e) => setForm((p) => ({ ...p, priceSub: e.target.value }))} /></div>
          </div>
          <div className="mform__row">
            <div className="mform__field"><label className="mform__label">{t.lblBoard}</label><input className="mform__input" value={form.boardSeatsText} onChange={(e) => setForm((p) => ({ ...p, boardSeatsText: e.target.value }))} /></div>
            <div className="mform__field"><label className="mform__label">{t.lblOfficer}</label><input className="mform__input" value={form.officerSeatsText} onChange={(e) => setForm((p) => ({ ...p, officerSeatsText: e.target.value }))} /></div>
          </div>
          <div className="mform__row">
            <div className="mform__field"><label className="mform__label">{t.lblShareholder}</label><input className="mform__input" value={form.shareholderSeatsText} onChange={(e) => setForm((p) => ({ ...p, shareholderSeatsText: e.target.value }))} /></div>
            <div className="mform__field"><label className="mform__label">{t.lblSort}</label><input className="mform__input" type="number" value={form.sortOrder} onChange={(e) => setForm((p) => ({ ...p, sortOrder: e.target.value }))} /></div>
          </div>
          <div className="mform__row">
            <div className="mform__field mform__field--full"><label className="mform__label">{t.lblTagline}</label><input className="mform__input" value={form.tagline} onChange={(e) => setForm((p) => ({ ...p, tagline: e.target.value }))} /></div>
          </div>
          <div className="mform__row">
            <div className="mform__field"><label className="mform__label">{t.lblButtonText}</label><input className="mform__input" value={form.buttonText} onChange={(e) => setForm((p) => ({ ...p, buttonText: e.target.value }))} /></div>
            <div className="mform__field"><label className="mform__label">{t.lblButtonUrl}</label><input className="mform__input" value={form.buttonUrl} onChange={(e) => setForm((p) => ({ ...p, buttonUrl: e.target.value }))} /></div>
          </div>
          <div className="mform__row">
            <div className="mform__field mform__field--full"><label className="mform__label">{t.lblFeatures}</label><textarea className="mform__textarea" rows={4} value={form.featuresText} onChange={(e) => setForm((p) => ({ ...p, featuresText: e.target.value }))} /></div>
          </div>
          <div className="mform__row">
            <div className="mform__field"><label className="mform__label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><input type="checkbox" checked={form.isFeatured} onChange={(e) => setForm((p) => ({ ...p, isFeatured: e.target.checked }))} />{t.lblFeatured}</label></div>
            <div className="mform__field"><label className="mform__label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><input type="checkbox" checked={form.isActive} onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))} />{t.lblActive}</label></div>
          </div>
        </form>
      </Modal>

      <div className="list-page-card list-page-card--unified">
        <header className="list-page-card__header">
          <div className="list-page-card__titles">
            <span className="list-page-card__title"><Icon name="money" size={18} color="#00334a" style={{ verticalAlign: 'middle', marginLeft: 6 }} />{t.title}</span>
            <span className="list-page-card__sub">{t.sub}</span>
          </div>
          <div className="list-page-card__actions">
            <button type="button" className="btn btn--primary" onClick={openCreate}><Icon name="plus" size={14} color="#fff" /> {t.add}</button>
          </div>
        </header>

        {error && <div className="list-page-card__banner list-page-card__banner--error">{error}</div>}
        {loading && <div className="list-page-card__loading">{t.loading}</div>}

        {!loading && (
        <div className="table-card">
          <table className="data-table">
            <thead><tr><th style={{ width: 42 }}>#</th><th>{t.colName}</th><th>{t.colPrice}</th><th>{t.colStatus}</th><th>{t.colSort}</th><th>{t.colActions}</th></tr></thead>
            <tbody>
              {items.map((row, idx) => (
                <tr key={row.id}>
                  <td className="td-num">{idx + 1}</td>
                  <td>
                    <strong>{row.name}</strong>
                    <div className="users-email">{row.label}{row.isFeatured ? ` · ${t.featured}` : ''}</div>
                  </td>
                  <td>{row.price}</td>
                  <td><span className={`users-status users-status--${row.isActive ? 'active' : 'inactive'}`}>{row.isActive ? t.active : t.inactive}</span></td>
                  <td>{row.sortOrder}</td>
                  <td style={{ display: 'flex', gap: 8 }}>
                    <button type="button" className="btn btn--ghost" onClick={() => openEdit(row)}><Icon name="edit" size={13} /> {t.edit}</button>
                    <button type="button" className="btn btn--ghost" onClick={() => handleDelete(row)}><Icon name="trash" size={13} /> {t.remove}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {items.length === 0 && <div className="users-empty">{t.noResults}</div>}
        </div>
      )}
      </div>
    </div>
  );
}
